/**
 * BrandVisualHints
 *
 * Zeigt Farbvorschläge aus dem gespeicherten visuellen Markenprofil an,
 * wenn die Post-Generierung `brand_visual_hints` im Response zurückgibt.
 *
 * Verwendung:
 *   <BrandVisualHints hints={responseData.brand_visual_hints} />
 *
 * Props:
 *   hints – Object mit optionalen Feldern:
 *             { primary_color, secondary_color, background_color,
 *               logo_url, color_hint }
 *           oder null/undefined → Komponente rendert nichts (null)
 *
 * Darstellung:
 *   - Kleiner Farbchip (24 × 24 px) pro erkannter Farbe
 *   - Hex-Code in Mono-Schrift daneben
 *   - Kurzer Erklärungstext
 *   - Wird komplett ausgeblendet wenn keine Farben vorhanden
 */

/**
 * Einzelner Farbchip mit Label und Hex-Code.
 * Gibt null zurück wenn color fehlt.
 */
const ColorChip = ({ color, label }) => {
  if (!color) return null;
  return (
    <div className="flex items-center gap-1.5" title={`${label}: ${color}`}>
      <div
        className="w-6 h-6 rounded border border-stone-200 shadow-sm flex-shrink-0"
        style={{ backgroundColor: color }}
      />
      <span className="text-xs font-mono text-stone-500">{color}</span>
    </div>
  );
};

const BrandVisualHints = ({ hints }) => {
  if (!hints) return null;

  const { primary_color, secondary_color, background_color } = hints;
  const hasColors = primary_color || secondary_color || background_color;

  if (!hasColors) return null;

  return (
    <div className="flex flex-col gap-2 px-3 py-3 bg-violet-50 border border-violet-200 rounded-lg">
      {/* Titel-Zeile mit Tooltip */}
      <div className="flex items-center gap-2 text-xs text-violet-700 font-medium">
        <span aria-hidden="true">🎨</span>
        <span>Farbvorschlag für Bilder &amp; Thumbnails</span>
        <span
          className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-violet-200 text-violet-600 cursor-help text-[10px] font-bold flex-shrink-0"
          title="Diese Farben wurden automatisch aus deiner Website erkannt. Bitte kurz prüfen, bevor du sie für Designs nutzt."
          aria-label="Hinweis: Diese Farben wurden automatisch aus deiner Website erkannt. Bitte kurz prüfen, bevor du sie für Designs nutzt."
        >
          i
        </span>
      </div>

      {/* Farbchips */}
      <div className="flex flex-wrap gap-4">
        <ColorChip color={primary_color}    label="Primärfarbe"   />
        <ColorChip color={secondary_color}  label="Sekundärfarbe" />
        <ColorChip color={background_color} label="Hintergrund"   />
      </div>

      {/* Erklärungs-Text */}
      <p className="text-xs text-violet-500 leading-snug">
        Automatisch aus deiner Website erkannt — bitte kurz prüfen, bevor du sie für Designs nutzt.
      </p>
    </div>
  );
};

export default BrandVisualHints;
